import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  LayoutDashboard, Ticket, Users, BarChart3, Plus,
  LogOut, ChevronLeft, ChevronRight, Timer, KeyRound
} from "lucide-react";
import { ROLE_LABELS } from "@/utils/constants";

const NAV_ITEMS = [
  { path: "/dashboard", label: "Dashboard", icon: LayoutDashboard, roles: ["admin", "manager", "agent", "end_user"] },
  { path: "/tickets", label: "Tickets", icon: Ticket, roles: ["admin", "manager", "agent", "end_user"] },
  { path: "/tickets/new", label: "New Ticket", icon: Plus, roles: ["admin", "manager", "agent", "end_user"] },
  { divider: true },
  { path: "/users", label: "Users", icon: Users, roles: ["admin"] },
  { path: "/reports", label: "Reports", icon: BarChart3, roles: ["admin", "manager"] },
  { divider: true },
  { path: "/settings/sla", label: "SLA Settings", icon: Timer, roles: ["admin"] },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="flex h-screen bg-[#FAFAFA]" data-testid="app-layout">
      <aside
        className={`${collapsed ? "w-[56px]" : "w-[220px]"} bg-white border-r border-[#E4E4E7] flex flex-col transition-all duration-200 ease-in-out shrink-0`}
        data-testid="sidebar"
      >
        {/* Logo */}
        <div className="h-14 flex items-center px-3 border-b border-[#E4E4E7] gap-2">
          {!collapsed && (
            <div className="flex items-center gap-2">
              <img src="/assets/aptransco-logo.png" alt="APTransco" className="h-8 w-8 object-contain shrink-0" />
              <div className="leading-none">
                <span className="font-heading text-[15px] font-black text-[#DC2626] tracking-tight">AP</span>
                <span className="font-heading text-[15px] font-black text-[#003DA5] tracking-tight">TRANSCO</span>
                <span className="block text-[9px] tracking-[0.15em] uppercase font-semibold text-[#71717A]">ServiceDesk</span>
              </div>
            </div>
          )}
          {collapsed && <img src="/assets/aptransco-logo.png" alt="APTransco" className="h-7 w-7 object-contain" />}
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="ml-auto p-1.5 hover:bg-zinc-100 rounded-md text-[#71717A] hover:text-[#09090B]"
            data-testid="sidebar-toggle"
          >
            {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 py-2 px-2 space-y-0.5 overflow-y-auto">
          {NAV_ITEMS.map((item, idx) => {
            if (item.divider) {
              return <div key={`div-${idx}`} className="my-2 mx-1 border-t border-[#F4F4F5]" />;
            }
            if (!item.roles.includes(user?.role)) return null;
            const isActive = location.pathname === item.path ||
              (item.path === "/tickets" && location.pathname.startsWith("/tickets") && location.pathname !== "/tickets/new");
            return (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                data-testid={`nav-${item.path.replace(/\//g, "-").slice(1)}`}
                className={`w-full flex items-center gap-2.5 px-2.5 py-2 text-[13px] rounded-md relative ${
                  isActive
                    ? "nav-active bg-[#003DA5]/[0.07] text-[#003DA5] font-semibold"
                    : "text-[#52525B] hover:bg-[#F4F4F5] hover:text-[#09090B] font-medium"
                }`}
              >
                <item.icon size={17} strokeWidth={isActive ? 2.2 : 1.8} className="shrink-0" />
                {!collapsed && <span className="truncate">{item.label}</span>}
              </button>
            );
          })}
        </nav>

        {/* User */}
        <div className="border-t border-[#E4E4E7] p-2.5">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className={`w-full flex items-center ${collapsed ? "justify-center" : "gap-2.5"} rounded-md p-1 hover:bg-[#F4F4F5] transition-colors`}
                data-testid="user-menu-trigger"
              >
                <Avatar className="h-8 w-8 shrink-0">
                  <AvatarFallback className="bg-[#003DA5] text-white text-xs font-bold">
                    {user?.name?.charAt(0)?.toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                {!collapsed && (
                  <div className="flex-1 min-w-0 text-left">
                    <p className="text-sm font-medium text-[#09090B] truncate leading-tight">{user?.name}</p>
                    <p className="text-[10px] tracking-[0.12em] uppercase text-[#71717A] font-semibold">
                      {ROLE_LABELS[user?.role]}
                    </p>
                  </div>
                )}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent side="top" align="start" className="w-48">
              <DropdownMenuItem
                onClick={() => navigate("/settings/change-password")}
                data-testid="menu-change-password"
                className="cursor-pointer gap-2 text-[13px]"
              >
                <KeyRound size={14} />
                Change Password
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={handleLogout}
                data-testid="logout-button"
                className="cursor-pointer gap-2 text-[13px] text-red-600 focus:text-red-600"
              >
                <LogOut size={14} />
                Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      <main className="flex-1 overflow-auto" data-testid="main-content">
        {children}
      </main>
    </div>
  );
}
